import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { Fulfillment } from 'api/models/Fulfillment';
import { UserProfile } from 'api/models/UserProfile';
import { UsersService } from 'api/services/UsersService';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { userSearchQueryKey, userSearchStaleTime } from '../ModelStatus.constants';

interface AssignUsersDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (reviewers: number[], preparers: number[]) => Promise<void>;
  fulfillmentData?: Fulfillment;
  title: string;
  availableUsers: { reviewers: UserProfile[], preparers: UserProfile[] };
  getUserDisplayName: (user: UserProfile) => string;
}

const AssignUsersDialog: React.FC<AssignUsersDialogProps> = ({
  open,
  onClose,
  onSave,
  fulfillmentData,
  title,
  availableUsers,
  getUserDisplayName,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const [selectedReviewers, setSelectedReviewers] = useState<number[]>([]);
  const [selectedPreparers, setSelectedPreparers] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const { data: searchUsers = [], isLoading: isSearching } = useQuery({
    queryKey: [userSearchQueryKey, debouncedSearchTerm],
    queryFn: () => UsersService.apiUsersList(debouncedSearchTerm.length > 2 ? debouncedSearchTerm : undefined),
    enabled: open && debouncedSearchTerm.length > 2,
    staleTime: userSearchStaleTime,
  });

  const allAvailableUsers = useMemo(() => {
    const reviewers = availableUsers?.reviewers || [];
    const preparers = availableUsers?.preparers || [];
    const assignedUsers = [...reviewers, ...preparers];
    const searchStaffUsers = searchUsers.filter(user => user.is_staff);
    
    // Combine and deduplicate users
    return [...assignedUsers, ...searchStaffUsers];
  }, [availableUsers, searchUsers]);

  // Filter available users based on search term and staff status
  const filteredUsers = useMemo(() => allAvailableUsers.filter(user => {
    if (!(user as any).is_staff) {
      return false;
    }
      
    if (!debouncedSearchTerm || debouncedSearchTerm.length < 3) {
      return true; // Show all staff users when not searching
    }
      
    const searchLower = debouncedSearchTerm.toLowerCase();
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
    const email = user.email?.toLowerCase() || '';
      
    return fullName.includes(searchLower) || 
             email.includes(searchLower)
  }), [allAvailableUsers, debouncedSearchTerm]);

  useEffect(() => {
    if (fulfillmentData && open) {
      const reviewers = fulfillmentData.reviewers as UserProfile[];
      const preparers = fulfillmentData.preparers as UserProfile[];
      
      const reviewerIds = reviewers?.map((user: UserProfile) => user.id).filter((id): id is number => id !== undefined) || [];
      const preparerIds = preparers?.map((user: UserProfile) => user.id).filter((id): id is number => id !== undefined) || [];
      
      setSelectedReviewers(reviewerIds);
      setSelectedPreparers(preparerIds);
    }
  }, [fulfillmentData, open]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setSelectedReviewers([]);
      setSelectedPreparers([]);
      setIsSaving(false);
    }
  }, [open]);

  const handleReviewerToggle = useCallback((userId: number) => {
    setSelectedReviewers(prev => (
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    ));
  }, []);

  const handlePreparerToggle = useCallback((userId: number) => {
    setSelectedPreparers(prev => (
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    ));
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await onSave(selectedReviewers, selectedPreparers);
      onClose();
    } catch {
      enqueueSnackbar('Failed to assign users. Please try again.', { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [selectedReviewers, selectedPreparers, onSave, onClose]);

  const getSelectedUserNames = (userIds: number[], role: string): string[] => 
    userIds
      .map(id => {
        const user = allAvailableUsers.find(u => u.id === id);
        return user ? getUserDisplayName(user as UserProfile) : `${role} ID: ${id}`;
      });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonIcon />
          {title}
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Search Field */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search staff members..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          helperText="Enter at least 3 characters to filter staff members."
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />

        {/* Selected Users Summary */}
        {(selectedReviewers.length > 0 || selectedPreparers.length > 0) && (
          <Box sx={{ mb: 2 }}>
            {selectedReviewers.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Reviewers:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {getSelectedUserNames(selectedReviewers, 'Reviewer').map(name => (
                    <Chip key={name} label={name} size="small" color="primary" />
                  ))}
                </Box>
              </Box>
            )}

            {selectedPreparers.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Preparers:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {getSelectedUserNames(selectedPreparers, 'Preparer').map(name => (
                    <Chip key={name} label={name} size="small" color="secondary" />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Users List */}
        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
          {isSearching && (
            <ListItem>
              <ListItemText
                primary="Searching staff members..."
                secondary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={16} />
                    <Typography variant="caption">Please wait</Typography>
                  </Box>
                }
              />
            </ListItem>
          )}
          
          {!isSearching && filteredUsers.length === 0 && (
            <ListItem>
              <ListItemText
                primary={searchTerm ? 'No staff members found' : 'No staff members available'}
                secondary={searchTerm ? 'Try a different search term' : 'All available staff members are shown'}
              />
            </ListItem>
          )}
          
          {!isSearching && filteredUsers.length > 0 && (
            filteredUsers.map(user => (
              <ListItem key={user.id} divider secondaryAction={
                <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                  <Box display="flex" alignItems="center">
                    <Typography variant="caption" sx={{ mr: 1, minWidth: 60 }}>
                      Reviewer
                    </Typography>
                    <Checkbox
                      edge="end"
                      checked={selectedReviewers.includes(user.id!)}
                      onChange={() => handleReviewerToggle(user.id!)}
                      color="primary"
                    />
                  </Box>
                  <Box display="flex" alignItems="center">
                    <Typography variant="caption" sx={{ mr: 1, minWidth: 60 }}>
                      Preparer
                    </Typography>
                    <Checkbox
                      edge="end"
                      checked={selectedPreparers.includes(user.id!)}
                      onChange={() => handlePreparerToggle(user.id!)}
                      color="secondary"
                    />
                  </Box>
                </Box>
              }>
                <ListItemText
                  primary={getUserDisplayName(user as UserProfile)}
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                          Staff Member
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))
          )}
        </List>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving || (selectedReviewers.length === 0 && selectedPreparers.length === 0)}
          startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
        >
          {isSaving ? 'Assigning...' : 'Assign Users'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignUsersDialog;
